/**
 * Customer-facing wording for an order (KAN-926) — shared by the thank-you
 * page, the order history and the tenant admin order detail so the three
 * never drift apart again (KAN-752 fixed the configurator and the cart;
 * these surfaces kept printing ``color_code: cerna`` and ``standardni``).
 *
 * Labels come FROM THE ORDER RESPONSE (``dimension_labels`` next to each
 * line's ``dimensions``, ``delivery_selection`` next to ``delivery_code``).
 * Nothing here knows a process slug, a dimension key or a tier code by
 * name; a value nobody labelled falls back to the caller's vocabulary,
 * never to the wire key.
 */

import type { OrderDeliveryView, OrderLineDimensionLabel } from "@/api/types";
import {
  formatDimensions,
  type DimensionLabelMap,
} from "@/lib/dimensionDisplay";
import { humanize } from "@/lib/labels";

interface LabelledLine {
  dimensions: Record<string, unknown>;
  dimension_labels?: Record<string, OrderLineDimensionLabel>;
}

/** Keys the BE flagged as the process-modifier (finish) slot. */
export function orderLineModifierKeys(line: LabelledLine): string[] {
  return Object.entries(line.dimension_labels ?? {})
    .filter(([, meta]) => meta.is_modifier)
    .map(([key]) => key);
}

/**
 * Build the {@link DimensionLabelMap} for one order line.
 *
 * ``modifierLabel`` names the modifier slot („Úprava" / „Finish") — the BE
 * cannot label it because the slot is a wire convention, not a dimension.
 */
export function orderLineLabelMap(
  line: LabelledLine,
  modifierLabel: string,
): DimensionLabelMap {
  const map: DimensionLabelMap = new Map();
  for (const [key, meta] of Object.entries(line.dimension_labels ?? {})) {
    map.set(key, {
      label: meta.label || (meta.is_modifier ? modifierLabel : ""),
      unit: meta.unit ?? null,
      valueLabel: meta.value_label ?? null,
    });
  }
  return map;
}

/**
 * „Barva: Černá · Výplň: 20 % · Výška vrstvy: 0.2 mm" for one line.
 * ``withModifier: false`` leaves the modifier slot out — for tables that
 * render it in its own column (admin order detail).
 */
export function formatOrderLineDimensions(
  line: LabelledLine,
  modifierLabel: string,
  {
    withModifier = true,
    locale,
    extraLabels,
  }: {
    withModifier?: boolean;
    /** Formats numbers for the reader („0,2"); omitted = raw. */
    locale?: string;
    /** Field-level labels for keys the line itself does not label. */
    extraLabels?: DimensionLabelMap;
  } = {},
): string {
  const labels = orderLineLabelMap(line, modifierLabel);
  for (const [key, meta] of extraLabels ?? []) {
    const own = labels.get(key);
    labels.set(key, {
      ...meta,
      ...own,
      label: own?.label || meta.label,
      unit: own?.unit ?? meta.unit,
    });
  }
  let dims = line.dimensions ?? {};
  if (!withModifier) {
    const modifierKeys = new Set(orderLineModifierKeys(line));
    dims = Object.fromEntries(
      Object.entries(dims).filter(([key]) => !modifierKeys.has(key)),
    );
  }
  return formatDimensions(dims, labels, locale);
}

/**
 * The modifier's human name („Černá", „Zinkování") or "" when the line has
 * none. Falls back to the humanized code so the cell never goes blank for
 * a modifier the tenant deleted since.
 */
export function formatOrderLineModifier(line: LabelledLine): string {
  for (const key of orderLineModifierKeys(line)) {
    const raw = line.dimensions?.[key];
    if (raw == null || raw === "") continue;
    return line.dimension_labels?.[key]?.value_label || humanize(String(raw));
  }
  return "";
}

/**
 * The delivery speed as the tenant named it — ``delivery_selection.label``
 * (the server resolves it from the frozen snapshot, KAN-1957).
 * ``delivery_code`` is a tier CODE (``standardni``), never shown; a legacy
 * order without a snapshot gets the i18n name of its code via
 * ``translateCode`` (or the humanized code).
 */
export function orderDeliveryLabel(
  selection: OrderDeliveryView | null | undefined,
  deliveryCode: string,
  translateCode?: (code: string) => string | undefined,
): string {
  const authored = selection?.label;
  if (authored) return authored;
  return translateCode?.(deliveryCode) || humanize(deliveryCode);
}
